import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'wouter'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Icon } from '@/components/ds/Icon'
import { api, ApiError } from '@/lib/api'
import { useCargos } from '@/hooks/useCargos'
import { useUser } from '@/contexts/UserContext'
import { whatsappSoporte } from '@/lib/support'
import { inicialesEmpresa } from '@/lib/empresa-marca'
import { cn } from '@/lib/utils'

// Pantalla única de onboarding: los datos de la empresa y el cargo del owner en
// un solo formulario con un solo submit. No hay pasos, no hay "saltar por
// ahora", y el botón de continuar nunca se oculta — su estado apagado es la
// señal de que falta algo, y el clic sirve para descubrir qué.
//
// Todos los campos activos son obligatorios: sin ellos el sistema no puede
// emitir documentos con la identidad correcta ni enviar los avisos. Esa
// obligatoriedad vive en el formulario y no en la base: las columnas siguen
// siendo nullable porque el signup del dueño crea la empresa con lo mínimo. Por
// eso tampoco hay asteriscos en los labels: se dice una sola vez, en la barra
// de progreso.
//
// TODO test: muestra skeleton mientras carga empresa + persona + cargos en paralelo
// TODO test: precarga los datos de empresa y el cargo actual de la persona
// TODO test: un campo vacío no muestra error hasta el blur o hasta intentar enviar
// TODO test: el chip de cada bloque cuenta sólo los campos activos
// TODO test: desmarcar "usar este correo" prellena los dos buzones con el de contacto una sola vez
// TODO test: volver a marcarlo conserva en memoria lo escrito en los buzones
// TODO test: con el check marcado se envían los tres correos con el valor de contacto
// TODO test: "Crear mi cargo" reemplaza el select por nombre + descripción y limpia la bandera de intento
// TODO test: en modo cargo propio el submit hace POST /cargos antes del PUT de persona
// TODO test: si PUT /me/empresa falla, no se ejecuta PUT /personas/{id}; reintento reenvía ambos
// TODO test: si el cargo ya se creó, un reintento no vuelve a crearlo (no duplica)
// TODO test: el preview del aside usa la misma función de iniciales que la topbar
// TODO test: si persona_id es null, muestra error duro de soporte sin ningún botón de salida
// TODO test: esta pantalla NO renderiza ningún botón "Saltar por ahora"

/* -------------------------------------------------------------------------- */
/* Catálogo de campos                                                          */
/* -------------------------------------------------------------------------- */

type CampoId =
  | 'razon_social'
  | 'nombre_fantasia'
  | 'giro'
  | 'email'
  | 'telefono'
  | 'direccion'
  | 'email_para_registros'
  | 'email_para_vencimientos'
  | 'cargo'
  | 'cargo_nombre'
  | 'cargo_descripcion'

interface DefinicionCampo {
  id: CampoId
  label: string
  /** Glifo guía dentro del control. El teléfono no lleva: ya tiene el prefijo. */
  icono?: string
  placeholder: string
  /** Aclaración al lado del label: dónde encontrar el dato. */
  hint?: string
  /** Consecuencia visible del dato, bajo el control. */
  nota?: string
  /** Ocupa el ancho completo de la tarjeta. */
  ancho?: boolean
  prefijo?: string
  select?: boolean
  area?: boolean
}

const CAMPOS: Record<CampoId, DefinicionCampo> = {
  razon_social: {
    id: 'razon_social',
    label: 'Razón social',
    icono: 'domain',
    placeholder: 'Nombre legal de la empresa',
    hint: 'como aparece en el SII',
    ancho: true,
  },
  nombre_fantasia: {
    id: 'nombre_fantasia',
    label: 'Nombre de fantasía',
    icono: 'badge',
    placeholder: 'Cómo la conocen',
    nota: 'Es el nombre que verás en la app y en los documentos.',
  },
  // Texto libre a propósito: no hay lista de rubros. El usuario copia el giro
  // tal como está en el SII, que es el único lugar donde ese dato es canónico.
  giro: {
    id: 'giro',
    label: 'Giro',
    icono: 'work',
    placeholder: 'Ej: Consultoría en prevención de riesgos',
    hint: 'como está en el SII',
  },
  email: {
    id: 'email',
    label: 'Email de contacto',
    icono: 'mail',
    placeholder: 'correo@empresa.cl',
    nota: 'Aparece en los documentos como correo oficial de la empresa.',
    ancho: true,
  },
  telefono: {
    id: 'telefono',
    label: 'Teléfono',
    placeholder: '9 1234 5678',
    prefijo: '+56',
  },
  direccion: {
    id: 'direccion',
    label: 'Dirección',
    icono: 'location_on',
    placeholder: 'Calle y número',
  },
  email_para_registros: {
    id: 'email_para_registros',
    label: 'Registros firmados',
    icono: 'mail',
    placeholder: 'correo@empresa.cl',
  },
  email_para_vencimientos: {
    id: 'email_para_vencimientos',
    label: 'Vencimientos',
    icono: 'mail',
    placeholder: 'correo@empresa.cl',
  },
  cargo: {
    id: 'cargo',
    label: 'Tu cargo',
    icono: 'person',
    placeholder: 'Elige tu cargo',
    select: true,
    ancho: true,
  },
  cargo_nombre: {
    id: 'cargo_nombre',
    label: 'Nombre del cargo',
    icono: 'person',
    placeholder: 'Ej: Encargado de flota',
    hint: 'así lo verá tu equipo',
    ancho: true,
  },
  cargo_descripcion: {
    id: 'cargo_descripcion',
    label: 'Qué hace en la empresa',
    placeholder:
      'Ej: Mantiene al día la documentación de vehículos y maquinaria, y coordina las revisiones técnicas.',
    nota: 'Una o dos líneas. Sirve para saber qué documentos y permisos le corresponden.',
    area: true,
    ancho: true,
  },
}

/** Los dos buzones que aparecen al desmarcar "usar este correo para todo". */
const BUZONES: { id: CampoId; icono: string; descripcion: string }[] = [
  {
    id: 'email_para_registros',
    icono: 'draw',
    descripcion: 'Recibe copia oculta de cada documento firmado en terreno.',
  },
  {
    id: 'email_para_vencimientos',
    icono: 'notifications_active',
    descripcion: 'Avisa antes de que venza un documento. El trabajador recibe copia de los suyos.',
  },
]

const BENEFICIOS = [
  {
    icono: 'notifications_active',
    titulo: 'Alertas de vencimiento',
    detalle: 'Avisamos al correo de la empresa antes de que caduque un documento.',
  },
  {
    icono: 'description',
    titulo: 'Documentos con tus datos',
    detalle: 'Actas, checklists y certificados salen con la razón social y dirección.',
  },
  {
    icono: 'groups',
    titulo: 'Tu equipo desde el día uno',
    detalle: 'Tu cargo define qué puede hacer cada usuario que invites después.',
  },
]

const MIN_DESCRIPCION_CARGO = 15
const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

type Valores = Record<CampoId, string>

const VALORES_VACIOS: Valores = {
  razon_social: '',
  nombre_fantasia: '',
  giro: '',
  email: '',
  telefono: '',
  direccion: '',
  email_para_registros: '',
  email_para_vencimientos: '',
  cargo: '',
  cargo_nombre: '',
  cargo_descripcion: '',
}

/* -------------------------------------------------------------------------- */
/* Validación                                                                  */
/* -------------------------------------------------------------------------- */

function soloDigitos(valor: string): string {
  return valor.replace(/\D/g, '')
}

/** Devuelve el mensaje de error del campo, o "" si el dato es válido. */
function errorDe(id: CampoId, valores: Valores): string {
  const valor = valores[id].trim()
  if (!valor) return 'Completa este dato'
  if (id.startsWith('email') && !RE_EMAIL.test(valor)) return 'Revisa el formato del correo'
  if (id === 'telefono' && soloDigitos(valor).length < 8) return 'Faltan dígitos'
  if (id === 'cargo_descripcion' && valor.length < MIN_DESCRIPCION_CARGO)
    return 'Cuenta en una línea qué hace'
  return ''
}

/**
 * El teléfono se guarda normalizado como `+56` + dígitos, así que el prefijo
 * nunca viaja como texto libre. Al cargar hay que quitarlo para que el campo
 * muestre sólo la parte que el usuario escribe — si no, el "+56" del recuadro y
 * el del valor quedarían duplicados en pantalla.
 */
function telefonoSinPrefijo(valor: string | null): string {
  const digitos = soloDigitos(valor ?? '')
  return digitos.startsWith('56') ? digitos.slice(2) : digitos
}

/* -------------------------------------------------------------------------- */
/* Tipos de la API                                                             */
/* -------------------------------------------------------------------------- */

interface EmpresaApiResponse {
  data: {
    rut: string
    nombre_fantasia: string
    razon_social: string | null
    email: string | null
    telefono: string | null
    giro: string | null
    direccion: string | null
    email_para_registros: string | null
    email_para_vencimientos: string | null
  }
}

interface PersonaData {
  nombre: string
  apellido: string
  rut: string
  email: string
}

interface PersonaResponse {
  data: PersonaData & {
    id: number
    // GET devuelve la relación expandida; el PUT en cambio recibe cargo_id plano.
    cargo: { id: number; nombre: string } | null
    [key: string]: unknown
  }
}

interface CargoResponse {
  data: { id: number; nombre: string }
}

/* -------------------------------------------------------------------------- */
/* Piezas de presentación                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Anatomía de un campo: label + hint arriba, el check teal a la derecha en
 * cuanto el dato es válido, el control, y debajo el error o la nota — nunca los
 * dos. El check es la única señal positiva: no se pintan bordes verdes.
 */
function Campo({
  definicion,
  children,
  listo,
  error,
}: {
  definicion: DefinicionCampo
  children: React.ReactNode
  listo: boolean
  error: string
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', definicion.ancho && 'col-span-full')}>
      <div className="flex items-baseline gap-[7px]">
        <label htmlFor={definicion.id} className="text-[12.5px] font-medium text-strong">
          {definicion.label}
        </label>
        {definicion.hint && <span className="text-[11px] text-soft">{definicion.hint}</span>}
        <span className="flex-1" />
        {listo && <Icon name="check_circle" size={15} className="text-primary" />}
      </div>

      {children}

      {error ? (
        <MensajeError texto={error} />
      ) : (
        definicion.nota && (
          <span className="text-[11px] leading-[1.45] text-soft">{definicion.nota}</span>
        )
      )}
    </div>
  )
}

function MensajeError({ texto }: { texto: string }) {
  return (
    <div className="flex items-center gap-[5px]">
      <Icon name="error" size={14} className="text-destructive" />
      <span className="text-[11px] text-destructive">{texto}</span>
    </div>
  )
}

/**
 * Recuadro del control de texto. El borde y el foco viven acá y no en el
 * `<input>` porque adentro conviven el prefijo y el ícono guía: si el borde
 * fuera del input, el anillo de foco encerraría sólo una parte del control.
 */
function Recuadro({
  children,
  estado,
}: {
  children: React.ReactNode
  estado: 'vacio' | 'lleno' | 'error'
}) {
  return (
    <div
      className={cn(
        'flex h-9 items-center overflow-hidden rounded-lg border bg-background transition-colors',
        'focus-within:border-primary focus-within:ring-2 focus-within:ring-ring',
        estado === 'error' && 'border-destructive',
        estado === 'lleno' && 'border-input-filled',
        estado === 'vacio' && 'border-input',
      )}
    >
      {children}
    </div>
  )
}

/** Chip de progreso en la cabecera de cada bloque. */
function ChipBloque({ listos, total }: { listos: number; total: number }) {
  const completo = listos === total
  return (
    <span
      className={cn(
        'inline-flex h-[21px] shrink-0 items-center gap-[5px] rounded-sm px-2 text-[11px] font-medium whitespace-nowrap',
        completo ? 'bg-success-bg text-success' : 'bg-muted text-meta',
      )}
    >
      <Icon name={completo ? 'check_circle' : 'radio_button_unchecked'} size={14} />
      {completo ? 'Listo' : `${listos} de ${total}`}
    </span>
  )
}

interface OnboardingSetupScreenProps {
  // Se recibe desde el wrapper (OnboardingWizard), que ya mantiene su propia
  // instancia de useSetupStatus() para los estados de loading/error iniciales.
  // Evita disparar un segundo fetch de setup-status en paralelo.
  onInvalidateSetupStatus: () => Promise<void>
}

/* -------------------------------------------------------------------------- */
/* Pantalla                                                                    */
/* -------------------------------------------------------------------------- */

export function OnboardingSetupScreen({ onInvalidateSetupStatus }: OnboardingSetupScreenProps) {
  const [, navigate] = useLocation()
  const { user } = useUser()
  const { cargos, isLoading: loadingCargos, error: cargosError } = useCargos()

  // GAP-03: persona_id es opcional en AuthUser. Si es null, el owner no tiene
  // Persona vinculada — situación anómala (el signup crea la Persona del owner
  // automáticamente), pero se maneja explícitamente con un error duro sin salida.
  const personaId = user?.persona_id ?? null

  const [valores, setValores] = useState<Valores>(VALORES_VACIOS)
  // El rut no es editable en esta pantalla (viene del signup) pero el PUT lo requiere.
  const [empresaRut, setEmpresaRut] = useState('')
  const [persona, setPersona] = useState<PersonaData | null>(null)

  const [tocados, setTocados] = useState<Partial<Record<CampoId, boolean>>>({})
  const [intento, setIntento] = useState(false)
  const [cargoPropio, setCargoPropio] = useState(false)
  const [mismoCorreo, setMismoCorreo] = useState(true)

  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [erroresBackend, setErroresBackend] = useState<Partial<Record<CampoId, string>>>({})
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [listo, setListo] = useState(false)

  // Máquina de fallo parcial: el submit son hasta tres llamadas encadenadas y
  // cada una que sale bien no se repite en el reintento. El cargo es el caso
  // crítico — sin este flag, reintentar tras un fallo del PUT de persona
  // intentaría crear un segundo cargo con el mismo nombre, que el backend
  // rechaza por el unique de (empresa, nombre) y dejaría al usuario trabado.
  const [empresaGuardada, setEmpresaGuardada] = useState(false)
  const [cargoCreadoId, setCargoCreadoId] = useState<number | null>(null)

  /* --- carga inicial ----------------------------------------------------- */

  useEffect(() => {
    if (personaId === null) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setLoadError(null)

    Promise.all([
      api.get<EmpresaApiResponse>('/me/empresa'),
      api.get<PersonaResponse>(`/personas/${personaId}`),
    ])
      .then(([empresaRes, personaRes]) => {
        if (cancelled) return
        const d = empresaRes.data
        const p = personaRes.data

        setEmpresaRut(d.rut ?? '')
        setValores({
          ...VALORES_VACIOS,
          razon_social: d.razon_social ?? '',
          nombre_fantasia: d.nombre_fantasia ?? '',
          giro: d.giro ?? '',
          email: d.email ?? '',
          telefono: telefonoSinPrefijo(d.telefono),
          direccion: d.direccion ?? '',
          email_para_registros: d.email_para_registros ?? '',
          email_para_vencimientos: d.email_para_vencimientos ?? '',
          cargo: p.cargo != null ? String(p.cargo.id) : '',
        })

        // El check arranca marcado salvo que la empresa ya tenga un reparto de
        // correos distinto: en ese caso desmarcarlo es lo único que muestra esos
        // valores, y dejarlo marcado los pisaría en silencio al guardar.
        const registros = d.email_para_registros ?? ''
        const vencimientos = d.email_para_vencimientos ?? ''
        const contacto = d.email ?? ''
        const hayReparto =
          (registros !== '' && registros !== contacto) ||
          (vencimientos !== '' && vencimientos !== contacto)
        setMismoCorreo(!hayReparto)

        setPersona({ nombre: p.nombre, apellido: p.apellido, rut: p.rut, email: p.email })
      })
      .catch(() => {
        if (cancelled) return
        setLoadError('No se pudieron cargar los datos. Inténtalo de nuevo.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [personaId])

  /* --- derivados --------------------------------------------------------- */

  const idsRol: CampoId[] = cargoPropio ? ['cargo_nombre', 'cargo_descripcion'] : ['cargo']
  const idsBuzones: CampoId[] = mismoCorreo
    ? []
    : ['email_para_registros', 'email_para_vencimientos']

  const bloques = useMemo(
    () => [
      {
        clave: 'identidad' as const,
        titulo: 'Identidad de la empresa',
        descripcion: 'Va en los documentos que emite el sistema',
        icono: 'domain',
        ids: ['razon_social', 'nombre_fantasia', 'giro'] as CampoId[],
      },
      {
        clave: 'contacto' as const,
        titulo: 'Contacto',
        descripcion: mismoCorreo
          ? 'Dónde te avisamos y qué dirección se imprime'
          : 'Un correo distinto para cada tipo de aviso',
        icono: 'alternate_email',
        ids: ['email', 'telefono', 'direccion'] as CampoId[],
      },
      {
        clave: 'rol' as const,
        titulo: 'Tu rol',
        descripcion: cargoPropio
          ? 'Defines el cargo que no existe todavía'
          : 'Con esto creamos tu usuario',
        icono: 'person',
        ids: cargoPropio ? (['cargo_nombre', 'cargo_descripcion'] as CampoId[]) : (['cargo'] as CampoId[]),
      },
    ],
    [mismoCorreo, cargoPropio],
  )

  // Sólo cuentan los campos que están en pantalla: los buzones ocultos y la vía
  // de cargo no elegida no se validan ni entran en el progreso.
  const idsActivos: CampoId[] = [
    'razon_social',
    'nombre_fantasia',
    'giro',
    'email',
    'telefono',
    'direccion',
    ...idsBuzones,
    ...idsRol,
  ]

  const listos = idsActivos.filter((id) => !errorDe(id, valores)).length
  const total = idsActivos.length
  const completo = listos === total

  const fantasia = valores.nombre_fantasia.trim() || valores.razon_social.trim()
  const marca = inicialesEmpresa(valores.nombre_fantasia, valores.razon_social)
  const correoAvisos = (mismoCorreo ? valores.email : valores.email_para_vencimientos).trim()

  /* --- handlers ---------------------------------------------------------- */

  const setValor = useCallback((id: CampoId, valor: string) => {
    setValores((prev) => ({ ...prev, [id]: valor }))
    setErroresBackend((prev) => (prev[id] ? { ...prev, [id]: undefined } : prev))
    // Editar cualquier dato de la empresa invalida el guardado previo: el
    // próximo submit tiene que reenviarlo.
    setEmpresaGuardada(false)
    // Y editar el cargo propio invalida el cargo YA creado. Sin esto, un
    // reintento tras corregir el nombre reusaría el cargo anterior en silencio
    // y guardaría al usuario con un cargo que en pantalla dice otra cosa.
    if (id === 'cargo_nombre' || id === 'cargo_descripcion') setCargoCreadoId(null)
  }, [])

  const marcarTocado = useCallback((id: CampoId) => {
    setTocados((prev) => ({ ...prev, [id]: true }))
  }, [])

  /** Error visible: sólo tras el blur del campo o tras un intento de envío. */
  const errorVisible = (id: CampoId): string => {
    const delBackend = erroresBackend[id]
    if (delBackend) return delBackend
    const error = errorDe(id, valores)
    return (tocados[id] || intento) && error ? error : ''
  }

  const alternarMismoCorreo = () => {
    const ahora = !mismoCorreo
    setMismoCorreo(ahora)
    // Al abrir el reparto por primera vez los buzones se prellenan con el correo
    // de contacto, para editar en vez de tipear de cero. Si el usuario ya había
    // escrito algo, se respeta: volver a marcar el check no borra, sólo oculta,
    // y al desmarcar reaparece tal cual.
    if (!ahora && !valores.email_para_registros && !valores.email_para_vencimientos) {
      setValores((prev) => ({
        ...prev,
        email_para_registros: prev.email,
        email_para_vencimientos: prev.email,
      }))
    }
  }

  const alternarViaCargo = () => {
    setCargoPropio((antes) => !antes)
    // Los campos de la vía nueva parten neutros: no heredan el intento fallido
    // que se hizo sobre la otra vía.
    setIntento(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (enviando) return

    if (!completo) {
      // El botón apagado igual responde al clic: acá es donde el usuario
      // descubre qué le falta.
      setIntento(true)
      return
    }
    if (personaId === null || persona === null) return

    setGeneralError(null)
    setErroresBackend({})
    setEnviando(true)
    try {
      // 1. El cargo propio se crea antes que nada: si el nombre choca con uno
      // existente, se corrige sin haber tocado todavía los datos de la empresa.
      let cargoId: number | null = cargoCreadoId
      if (cargoPropio) {
        if (cargoId === null) {
          try {
            const res = await api.post<CargoResponse>('/cargos', {
              nombre: valores.cargo_nombre.trim(),
              descripcion: valores.cargo_descripcion.trim(),
            })
            cargoId = res.data.id
            setCargoCreadoId(cargoId)
          } catch (err) {
            if (err instanceof ApiError && err.errors?.nombre) {
              setErroresBackend({ cargo_nombre: (err.errors.nombre as string[])[0] })
            } else {
              setGeneralError(
                err instanceof ApiError
                  ? err.message
                  : 'No pudimos crear el cargo. Intenta de nuevo.',
              )
            }
            return
          }
        }
      } else {
        cargoId = Number(valores.cargo)
      }

      // 2. Datos de la empresa. Los tres correos viajan SIEMPRE poblados: el
      // check es azúcar de interfaz, no un modo de datos distinto — el modelo
      // siempre tiene tres casillas.
      if (!empresaGuardada) {
        try {
          await api.put('/me/empresa', {
            rut: empresaRut,
            razon_social: valores.razon_social.trim(),
            nombre_fantasia: valores.nombre_fantasia.trim(),
            giro: valores.giro.trim(),
            email: valores.email.trim(),
            telefono: `+56${soloDigitos(valores.telefono)}`,
            direccion: valores.direccion.trim(),
            email_para_registros: mismoCorreo
              ? valores.email.trim()
              : valores.email_para_registros.trim(),
            email_para_vencimientos: mismoCorreo
              ? valores.email.trim()
              : valores.email_para_vencimientos.trim(),
          })
          setEmpresaGuardada(true)
        } catch (err) {
          if (err instanceof ApiError && err.errors) {
            const delBackend: Partial<Record<CampoId, string>> = {}
            for (const [campo, mensajes] of Object.entries(err.errors)) {
              if (campo in VALORES_VACIOS) {
                delBackend[campo as CampoId] = (mensajes as string[])[0]
              }
            }
            if (Object.keys(delBackend).length > 0) {
              setErroresBackend(delBackend)
            } else {
              setGeneralError(err.message)
            }
          } else {
            setGeneralError('Ocurrió un error al guardar los datos de la empresa.')
          }
          return
        }
      }

      // 3. El cargo del owner. PersonaRequest exige nombre, apellido, rut y
      // email también en el PUT (no llevan 'sometimes'), por eso se cargaron en
      // el mount: mandarlos evita un 422.
      try {
        await api.put(`/personas/${personaId}`, {
          nombre: persona.nombre,
          apellido: persona.apellido,
          rut: persona.rut,
          email: persona.email,
          cargo_id: cargoId,
        })
      } catch (err) {
        setGeneralError(
          err instanceof ApiError ? err.message : 'Ocurrió un error al guardar tu cargo.',
        )
        return
      }

      await onInvalidateSetupStatus()
      setListo(true)
    } finally {
      setEnviando(false)
    }
  }

  /* --- estados de borde --------------------------------------------------- */

  // GAP-03: owner sin Persona vinculada — error duro, sin ningún control de salida.
  if (personaId === null) {
    return (
      <div className="mx-auto max-w-xl px-4 py-8 sm:px-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive-bg p-4 text-[13px] text-destructive">
          No tenemos vinculada tu persona al sistema. Contacta a soporte para resolver este
          problema.
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1080px] animate-pulse px-[22px] pt-[34px]">
        <div className="grid gap-[22px] min-[940px]:grid-cols-[320px_minmax(0,1fr)] min-[940px]:items-start">
          <div className="h-[320px] rounded-2xl bg-muted" />
          <div className="flex flex-col gap-3.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-[220px] rounded-2xl bg-muted" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-xl px-4 py-8 sm:px-6">
        <p className="text-[13px] text-destructive">{loadError}</p>
      </div>
    )
  }

  /* --- render ------------------------------------------------------------- */

  /** Control de un campo según su tipo. El layout externo lo pone `Campo`. */
  const control = (definicion: DefinicionCampo) => {
    const id = definicion.id
    const valor = valores[id]
    const error = errorVisible(id)
    const estado = error ? 'error' : valor.trim() ? 'lleno' : 'vacio'

    if (definicion.select) {
      return (
        <Select
          value={valor}
          onValueChange={(v) => setValor(id, v)}
          disabled={enviando || loadingCargos}
        >
          <SelectTrigger id={id} aria-invalid={!!error} className="h-9 w-full text-[13px]">
            <span className="flex min-w-0 items-center gap-2">
              <Icon name={definicion.icono!} size={17} className="text-icon-soft" />
              <SelectValue placeholder={definicion.placeholder} />
            </span>
          </SelectTrigger>
          <SelectContent>
            {cargos.map((cargo) => (
              <SelectItem key={cargo.id} value={String(cargo.id)}>
                {cargo.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }

    if (definicion.area) {
      const largo = valor.trim().length
      return (
        <div className="flex flex-col gap-[5px]">
          <Textarea
            id={id}
            value={valor}
            onChange={(e) => setValor(id, e.target.value)}
            onBlur={() => marcarTocado(id)}
            placeholder={definicion.placeholder}
            disabled={enviando}
            aria-invalid={!!error}
            rows={3}
            className="min-h-[74px] resize-y text-[13.5px] placeholder:text-icon-soft"
          />
          <span
            className={cn(
              'self-end font-mono text-[10.5px]',
              largo >= MIN_DESCRIPCION_CARGO ? 'text-success' : 'text-icon-soft',
            )}
          >
            {largo} / {MIN_DESCRIPCION_CARGO} mínimo
          </span>
        </div>
      )
    }

    return (
      <Recuadro estado={estado}>
        {definicion.prefijo && (
          <span className="flex h-full items-center border-r border-border-subtle bg-surface-muted px-2.5 font-mono text-[12.5px] text-muted-foreground">
            {definicion.prefijo}
          </span>
        )}
        {definicion.icono && (
          <Icon name={definicion.icono} size={17} className="ml-2.5 text-icon-soft" />
        )}
        <input
          id={id}
          value={valor}
          onChange={(e) => setValor(id, e.target.value)}
          onBlur={() => marcarTocado(id)}
          placeholder={definicion.placeholder}
          disabled={enviando}
          aria-invalid={!!error}
          inputMode={id === 'telefono' ? 'tel' : undefined}
          className="h-full min-w-0 flex-1 bg-transparent px-2.5 text-[13.5px] outline-none placeholder:text-icon-soft disabled:cursor-not-allowed disabled:opacity-50"
        />
      </Recuadro>
    )
  }

  return (
    <>
      <form onSubmit={handleSubmit} noValidate>
        {/* El padding inferior deja aire sobre la barra fija de progreso. En
            móvil la barra es más alta (el botón cae en su propia fila), así que
            necesita más margen o el último bloque queda debajo. */}
        <div className="px-[22px] pt-[34px] pb-[150px] sm:pb-[120px]">
          <div className="mx-auto grid max-w-[1080px] gap-[22px] min-[940px]:grid-cols-[320px_minmax(0,1fr)] min-[940px]:items-start">
            {/* --- aside: por qué pedimos esto y cómo se va a ver ---------- */}
            <div className="flex flex-col gap-3.5 min-[940px]:sticky min-[940px]:top-[78px]">
              <div className="rounded-2xl bg-secondary p-5 text-secondary-foreground">
                <span className="mb-3 inline-flex h-[22px] items-center gap-1.5 rounded-sm bg-secondary-hover px-[9px] text-[11px] font-semibold tracking-[0.04em] text-primary-on-dark">
                  <Icon name="domain_add" size={14} />
                  PRIMER PASO
                </span>
                <h1 className="mb-2 text-[23px] leading-[1.2] font-bold tracking-[-0.025em]">
                  Configuremos tu empresa
                </h1>
                <p className="mb-[18px] text-[13px] leading-relaxed text-on-dark-body text-pretty">
                  Con estos datos dejamos lista tu empresa, el correo que recibirá las alertas de
                  vencimiento y tu propio usuario. Toma dos minutos y después lo cambias cuando
                  quieras.
                </p>
                <div className="flex flex-col gap-[11px]">
                  {BENEFICIOS.map((beneficio) => (
                    <div key={beneficio.titulo} className="flex items-start gap-2.5">
                      <Icon name={beneficio.icono} size={18} className="mt-px text-primary-on-dark" />
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="text-[12.5px] font-semibold">{beneficio.titulo}</span>
                        <span className="text-[11.5px] leading-[1.45] text-on-dark-soft">
                          {beneficio.detalle}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Réplica del selector de empresa de la topbar. Se actualiza en
                  cada tecleo: lo que se ve acá es literalmente lo que el usuario
                  va a encontrar al entrar al panel — misma función de iniciales. */}
              <div className="rounded-2xl border bg-card px-4 py-3.5">
                <div className="mb-2.5 text-[11px] font-semibold tracking-[0.04em] text-meta">
                  ASÍ SE VERÁ EN LA APP
                </div>
                <div className="flex h-10 items-center gap-[9px] rounded-xl border pr-[9px] pl-[5px]">
                  <span
                    className={cn(
                      'flex size-7 shrink-0 items-center justify-center rounded-md text-[11.5px] font-semibold',
                      fantasia ? 'bg-primary text-primary-foreground' : 'bg-muted text-icon-soft',
                    )}
                  >
                    {marca}
                  </span>
                  <span className="flex min-w-0 flex-col leading-[1.25]">
                    <span className="text-[9.5px] font-semibold tracking-[0.07em] text-soft">
                      EMPRESA ACTIVA
                    </span>
                    <span
                      className={cn(
                        'truncate text-[13px] font-semibold tracking-[-0.01em]',
                        !fantasia && 'text-icon-soft',
                      )}
                    >
                      {fantasia || 'Nombre de tu empresa'}
                    </span>
                  </span>
                  <Icon name="unfold_more" size={18} className="ml-auto text-soft" />
                </div>
                <div className="mt-[11px] flex items-center gap-[7px] border-t border-border-subtle pt-[11px]">
                  <Icon name="mail" size={16} className="text-soft" />
                  <span className="min-w-0 truncate text-[11.5px] text-muted-foreground">
                    Alertas a{' '}
                    <strong className={cn('font-semibold', !correoAvisos && 'text-icon-soft')}>
                      {correoAvisos || 'el correo que indiques'}
                    </strong>
                  </span>
                </div>
              </div>
            </div>

            {/* --- formulario ---------------------------------------------- */}
            <div className="flex min-w-0 flex-col gap-3.5">
              {generalError && (
                <div className="rounded-lg border border-destructive/50 bg-destructive-bg p-3 text-[12.5px] text-destructive">
                  {generalError}
                </div>
              )}

              {bloques.map((bloque) => {
                const idsDelBloque =
                  bloque.clave === 'contacto' ? [...bloque.ids, ...idsBuzones] : bloque.ids
                const listosDelBloque = idsDelBloque.filter((id) => !errorDe(id, valores)).length
                const completoElBloque = listosDelBloque === idsDelBloque.length

                return (
                  <div key={bloque.clave} className="overflow-hidden rounded-2xl border bg-card">
                    <div className="flex items-center gap-2.5 border-b border-border-subtle px-[18px] py-[13px]">
                      <Icon
                        name={bloque.icono}
                        size={18}
                        className={completoElBloque ? 'text-primary' : 'text-soft'}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold tracking-[-0.01em]">
                          {bloque.titulo}
                        </div>
                        <div className="text-[11.5px] leading-[1.4] text-meta">
                          {bloque.descripcion}
                        </div>
                      </div>
                      <ChipBloque listos={listosDelBloque} total={idsDelBloque.length} />
                    </div>

                    <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3.5 px-[18px] py-4">
                      {bloque.ids.map((id) => (
                        <Campo
                          key={id}
                          definicion={CAMPOS[id]}
                          listo={!errorDe(id, valores)}
                          error={errorVisible(id)}
                        >
                          {control(CAMPOS[id])}
                        </Campo>
                      ))}
                    </div>

                    {/* Panel de reparto de correos: sólo en Contacto. */}
                    {bloque.clave === 'contacto' && (
                      <div className="px-[18px] pb-4">
                        <div className="overflow-hidden rounded-xl border">
                          {/* Toda la fila del encabezado es clickeable. */}
                          <label
                            className={cn(
                              'flex cursor-pointer items-start gap-2.5 px-[13px] py-[11px] transition-colors',
                              mismoCorreo ? 'bg-primary-50' : 'bg-surface-muted',
                            )}
                          >
                            <Checkbox
                              checked={mismoCorreo}
                              onCheckedChange={alternarMismoCorreo}
                              disabled={enviando}
                              className="mt-px"
                            />
                            {/* `block` no: acá el contenedor es flex a propósito
                                (son dos líneas apiladas, no una frase). */}
                            <span className="flex min-w-0 flex-col gap-0.5">
                              <span className="text-[12.5px] font-medium">
                                Usar este correo para todos los avisos
                              </span>
                              <span className="text-[11px] leading-[1.45] text-meta">
                                {mismoCorreo
                                  ? 'Los registros firmados y los avisos de vencimiento llegan al mismo correo.'
                                  : 'Desmarcado: define a qué correo llega cada tipo de aviso.'}
                              </span>
                            </span>
                          </label>

                          {!mismoCorreo &&
                            BUZONES.map((buzon) => {
                              const definicion = CAMPOS[buzon.id]
                              const error = errorVisible(buzon.id)
                              return (
                                <div
                                  key={buzon.id}
                                  className="grid grid-cols-[minmax(0,1fr)] items-center gap-3 border-t border-border-subtle px-[13px] py-3 min-[940px]:grid-cols-[minmax(0,1fr)_minmax(200px,270px)]"
                                >
                                  <div className="flex min-w-0 flex-col gap-0.5">
                                    <div className="flex items-center gap-1.5">
                                      <Icon name={buzon.icono} size={16} className="text-soft" />
                                      <label htmlFor={buzon.id} className="text-[12.5px] font-medium">
                                        {definicion.label}
                                      </label>
                                      {!errorDe(buzon.id, valores) && (
                                        <Icon name="check_circle" size={15} className="text-primary" />
                                      )}
                                    </div>
                                    <span className="text-[11px] leading-[1.45] text-meta">
                                      {buzon.descripcion}
                                    </span>
                                  </div>
                                  <div className="flex min-w-0 flex-col gap-1">
                                    {control(definicion)}
                                    {error && <MensajeError texto={error} />}
                                  </div>
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    )}

                    {/* Pie del bloque de rol: alterna entre elegir y crear. */}
                    {bloque.clave === 'rol' && (
                      <div className="flex flex-wrap items-center gap-[9px] border-t border-border-subtle bg-surface-muted px-[18px] py-[11px]">
                        <span className="min-w-[160px] flex-1 text-[11.5px] leading-[1.45] text-muted-foreground">
                          {cargoPropio
                            ? 'Este cargo queda creado en tu empresa y podrás asignárselo a otros trabajadores.'
                            : '¿Tu cargo no está en la lista? Créalo con su nombre y una línea de qué hace.'}
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          onClick={alternarViaCargo}
                          disabled={enviando}
                          className={cn(
                            'shrink-0',
                            !cargoPropio && 'border-primary-border text-primary-hover',
                          )}
                        >
                          <Icon name={cargoPropio ? 'format_list_bulleted' : 'add'} size={16} />
                          {cargoPropio ? 'Elegir de la lista' : 'Crear mi cargo'}
                        </Button>
                      </div>
                    )}
                  </div>
                )
              })}

              {cargosError && !cargoPropio && (
                <p className="text-[12.5px] text-destructive">
                  No se pudieron cargar los cargos. Puedes crear el tuyo con «Crear mi cargo».
                </p>
              )}

              <div className="flex items-start gap-2.5 rounded-xl border bg-card px-3.5 py-3">
                <Icon name="lock" size={17} className="text-soft" />
                <span className="text-[11.5px] leading-normal text-muted-foreground">
                  Estos datos son de tu empresa y no se comparten con otras. Los usas para los
                  documentos que emite el sistema y para el correo de alertas.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* --- barra fija: progreso + envío -------------------------------- */}
        <div className="fixed inset-x-0 bottom-0 z-20 border-t bg-background shadow-[0_-1px_3px_rgba(24,24,27,.05)]">
          <div className="mx-auto flex max-w-[1080px] flex-wrap items-center gap-3.5 px-[22px] py-3">
            <div className="flex min-w-[180px] flex-1 flex-col gap-[5px]">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'text-[12.5px] font-medium',
                    completo ? 'text-success' : 'text-strong',
                  )}
                >
                  {completo ? 'Todo listo' : `${listos} de ${total} datos`}
                </span>
                <span className="text-[11.5px] text-soft">
                  {completo ? 'puedes continuar' : 'todos son necesarios para partir'}
                </span>
              </div>
              <div className="h-1.5 max-w-[280px] overflow-hidden rounded-full bg-border-subtle">
                <div
                  className={cn(
                    'h-full rounded-full transition-[width] duration-200',
                    completo
                      ? 'bg-success-bar'
                      : listos > total / 2
                        ? 'bg-primary'
                        : 'bg-border-strong',
                  )}
                  style={{ width: `${Math.round((listos / total) * 100)}%` }}
                />
              </div>
            </div>

            {/* El botón nunca desaparece ni se bloquea mientras falten datos:
                apagado es la señal de que falta algo, y el clic es lo que
                revela cuáles. Por eso `aria-disabled` y no `disabled`. */}
            <Button
              type="submit"
              aria-disabled={!completo || enviando}
              className={cn(
                // Bajo 640px el botón cae en su propia fila por el wrap: ahí va
                // a ancho completo. Con `w-auto` quedaba chico y pegado a la
                // izquierda, con menos peso visual que la barra de progreso que
                // tiene encima — justo al revés de lo que corresponde.
                'w-full shrink-0 sm:w-auto',
                !completo &&
                  'cursor-not-allowed bg-muted text-soft shadow-none hover:bg-muted focus-visible:ring-border-strong',
              )}
            >
              {enviando ? (
                <>
                  <Icon name="progress_activity" size={17} className="animate-fc-spin" />
                  Guardando…
                </>
              ) : (
                <>
                  Guardar y continuar
                  <Icon name="arrow_forward" size={17} />
                </>
              )}
            </Button>
          </div>
        </div>
      </form>

      {/* --- confirmación ------------------------------------------------- */}
      {/* Sin "Cancelar" y sin cierre por ESC: a esta altura el guardado ya
          ocurrió y la única continuación posible es entrar al panel. */}
      <Dialog open={listo}>
        <DialogContent
          showCloseButton={false}
          onEscapeKeyDown={(e) => e.preventDefault()}
          className="animate-fc-in p-6 text-center sm:max-w-[420px]"
        >
          <div className="mx-auto mb-3.5 flex size-[46px] items-center justify-center rounded-full bg-success-bg">
            <Icon name="check_circle" size={26} className="text-success" />
          </div>
          <DialogTitle className="mb-1.5 text-[17px] font-semibold tracking-[-0.015em]">
            {fantasia} quedó lista
          </DialogTitle>
          <p className="mb-[18px] text-[12.5px] leading-relaxed text-muted-foreground">
            Ya puedes cargar tus trabajadores y documentos. Las alertas de vencimiento llegarán a{' '}
            {correoAvisos}.
          </p>
          <Button className="w-full" onClick={() => navigate('/?wizard=completado')}>
            Entrar al panel
          </Button>
          <p className="mt-3 text-[11.5px] leading-[1.45] text-muted-foreground">
            ¿Prefieres que te acompañemos?{' '}
            <a
              href={whatsappSoporte('Hola, necesito ayuda con la configuración de mi empresa.')}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium"
            >
              Escríbenos
            </a>{' '}
            y coordinamos contigo la puesta en marcha.
          </p>
        </DialogContent>
      </Dialog>
    </>
  )
}
